import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Error } from './Error'
import { ErrorCode } from '../constants/errorCodes'
import { Form } from './Form'
import { FintecWidgetProps } from '../types/tink.interface'
import { useTheme } from '../hooks/useTheme'
import publishEvent from '../api/publishEvent'
import { wizardSuccess } from '../api/wizard'
import { IbanGate } from './IbanGate'
import { allowedIbanCountries, defaultConfig } from '../constants'
import { useTinkLoader } from './TinkLoader'
import { useFTSWidgetContext } from '../context'
import { useTrack } from '../context/track'
import { TrackingEvent } from '../utils/tracking'
import { DACSteps } from '../constants/steps'
import { logError, logEvent } from '../utils/bugsnag/log'
import { BugSnagTrackingEvent } from '../utils/bugsnag/events'
import { WizardSuccessError } from '../utils/errors/WizardSuccessError'

const FintecWidget = memo(
  ({
    system: {
      clientUuid,
      environment,
      submitButtonStep1Label,
      submitButtonStep2Label,
      personId,
      origin,
      traversalUuid,
    },
    skipIbanGate,
    onSuccess,
    onError,
    onAbort,
    account = {},
    config = {},
    onStepChange,
  }: FintecWidgetProps) => {
    const { onBankCodeChange, iban, bankCode, type } = account
    const ftsConfig = useMemo(() => ({ ...defaultConfig, ...config }), [config])
    const { isThemeLoaded } = useTheme({ clientUuid })
    const [isError, setIsError] = useState(false)
    const track = useTrack()

    const {
      refetchKey,
      state: { transactionLocalData, transactionId, wizardKey },
      error: contextError,
    } = useFTSWidgetContext()
    const { tink, error } = useTinkLoader()

    const errorHandler = useCallback(
      (code: ErrorCode, messages: string[], isRecoverable: boolean) => {
        setIsError(true)
        if (onError) {
          onError(code, messages, isRecoverable)
        }

        if (!isRecoverable) {
          publishEvent(environment, 'failed', {
            ...transactionLocalData,
            transactionId,
          })
          refetchKey()
        } else if (code === ErrorCode.TRANSACTION_NOT_POSSIBLE) {
          // fetch key again if iban was provided before. Probably the iban does not work (e.g. wrong country code)
          refetchKey()
        }
      },
      [environment, onError, refetchKey, transactionId, transactionLocalData],
    )
    const successHandler = async () => {
      logEvent(
        'OnSuccess callback handler in fintech widget',
        BugSnagTrackingEvent.OnSuccessInternalCallback,
        {
          clientUuid,
          personId,
          origin,
          traversalUuid,
        },
      )

      try {
        // await because this request might get cancelled due to the redirect of Taurine/RR
        await wizardSuccess(environment, {
          applicationUuid: transactionLocalData.applicationUuid,
          clientUuid: transactionLocalData.clientUuid,
          customerUuid: transactionLocalData.customerUuid,
          loanLeadUuid: transactionLocalData.loanLeadUuid,
          origin: transactionLocalData.origin,
          personId: transactionLocalData.personId,
          transactionId,
          traversalUuid: transactionLocalData.traversalUuid,
        })
      } catch (e) {
        logError(
          new WizardSuccessError(
            'Wizard Success Error handler in Fintech Widget',
            clientUuid,
            personId,
            origin,
            transactionId,
            traversalUuid,
          ),
        )
      }

      publishEvent(environment, 'success', {
        ...transactionLocalData,
        transactionId,
      })
      if (onSuccess) {
        onSuccess(transactionId)
      }
    }

    const loginHandler = () => {
      publishEvent(environment, 'started', {
        ...transactionLocalData,
        transactionId,
      })
    }

    const accountSelectionHandler = () => {
      publishEvent(environment, 'accountSelection', {
        ...transactionLocalData,
        transactionId,
      })
    }

    const abortHandler = () => {
      publishEvent(environment, 'aborted', {
        ...transactionLocalData,
        transactionId,
      })
      if (onAbort) {
        onAbort()
      }
      refetchKey()
    }

    const renderHandler = () => {
      if (tink?.isLogin()) {
        track(TrackingEvent.DACBankLoginStepShown)
        onStepChange?.(DACSteps.BANK_LOGIN_STEP)
      }
      if (tink?.isBank()) {
        track(TrackingEvent.DACBankSelectionStepShown)
        onStepChange?.(DACSteps.BANK_SELECTION_STEP)
      }
      if (tink?.isTan()) {
        track(TrackingEvent.DACTanStepShown)
        onStepChange?.(DACSteps.TAN_STEP)
      }
    }

    useEffect(() => {
      if (!tink) {
        return
      }

      tink.configure(ftsConfig)
      tink.error(errorHandler)
      tink.login(loginHandler)
      tink.render(renderHandler)
      tink.finish(successHandler)
      tink.abort(abortHandler)
      tink.account(accountSelectionHandler)
      // tink.init()

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tink, transactionLocalData, transactionId])

    // sadly no useMemo hook here, bcs otherwise eslint doesn't recognize iban no neeing undefined in last return
    const hideIbanGate =
      type === 'bankCode' ||
      iban === undefined ||
      !allowedIbanCountries.includes(iban.substring(0, 2)) ||
      skipIbanGate

    useEffect(() => {
      if (!hideIbanGate) {
        onStepChange?.(DACSteps.IBAN_GATE_STEP)
      }
    }, [hideIbanGate, onStepChange])

    useEffect(() => {
      // If we hide IbanGate, we need to fetch the wizard key here
      // In case of error, we should not re-fetch here, re-fetchKey will be
      // triggered in the error handler based on the error type and the isKeyCustom flags.
      if (hideIbanGate && !wizardKey && !isError) {
        if (skipIbanGate && !!iban) {
          refetchKey(iban)
        } else {
          refetchKey()
        }
      }
    }, [iban, refetchKey, type, hideIbanGate, wizardKey, skipIbanGate, isError])

    if (hideIbanGate) {
      return (
        <>
          {(error || contextError) && <Error onRetry={refetchKey} />}
          <Form
            submitButtonStep1Label={submitButtonStep1Label}
            submitButtonStep2Label={submitButtonStep2Label}
            onBankCodeChange={onBankCodeChange}
            isThemeLoaded={isThemeLoaded}
            defaultValue={bankCode}
          />
        </>
      )
    }

    return (
      <>
        {(error || contextError) && <Error onRetry={refetchKey} />}
        <IbanGate iban={iban} onSubmit={refetchKey}>
          <Form
            submitButtonStep1Label={submitButtonStep1Label}
            submitButtonStep2Label={submitButtonStep2Label}
            onBankCodeChange={onBankCodeChange}
            isThemeLoaded={isThemeLoaded}
          />
        </IbanGate>
      </>
    )
  },
)

FintecWidget.displayName = 'FintecWidget'

export { FintecWidget }
